/**
 * @element qds-action-panel-items-editor
 *
 * Field-level editor for the `action-panel.items` config array. It keeps the
 * properties rail compact and edits items through a modal list/detail workflow.
 *
 * @property {string} property - Field name emitted with the edited value
 * @property {Array} value - Action items to edit
 * @property {object} metadata - Property-editor metadata
 * @property {object} context - Optional host context
 * @property {object} target - Optional object currently being edited
 * @property {boolean} readonly - Disables editing controls
 * @fires value-change - Committed action items ({ property, value })
 */

import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons, renderQdsIcon } from '../shared/icon-utils.js';
import '../property-editor/qds-property-editor.js';
import type { QdsConfigField } from '../../types/blocks.js';

loadBaseCSS();

const ACTION_PANEL_ITEMS_MIME = 'application/x-qds-action-panel-item';

interface QdsActionPanelItem {
  label: string;
  href: string;
  target: string;
  icon: string;
  iconSrc: string;
  iconAlt: string;
  actionKind: string;
  [key: string]: unknown;
}

type DragPayload = { index: number };

function cloneValue<T>(value: T): T {
  if (value === undefined || value === null) {
    return value;
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function toStringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function toActionPanelItem(value: unknown): QdsActionPanelItem {
  const source = value && typeof value === 'object'
    ? value as Record<string, unknown>
    : {};

  return {
    ...source,
    label: toStringValue(source.label, 'New action'),
    href: toStringValue(source.href, '#'),
    target: toStringValue(source.target, '_self') || '_self',
    icon: toStringValue(source.icon),
    iconSrc: toStringValue(source.iconSrc),
    iconAlt: toStringValue(source.iconAlt),
    actionKind: toStringValue(source.actionKind, 'forward') || 'forward'
  };
}

function createDefaultItem(): QdsActionPanelItem {
  return {
    label: 'New action',
    href: '#',
    target: '_self',
    icon: '',
    iconSrc: '',
    iconAlt: '',
    actionKind: 'forward'
  };
}

function parseItems(value: unknown): QdsActionPanelItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => toActionPanelItem(item));
}

export class QdsActionPanelItemsEditor extends HTMLElement {
  private _property = '';
  private _value: QdsActionPanelItem[] = [];
  private _metadata?: QdsConfigField;
  private _context?: unknown;
  private _target?: Record<string, unknown>;
  private _readonly = false;
  private _modalOpen = false;
  private _workingItems: QdsActionPanelItem[] = [];
  private _selectedIndex = -1;
  private _activeDropIndex: number | null = null;
  private _activeDragPayload: DragPayload | null = null;
  private _advancedOpen = false;
  private _jsonDraft = '[]';
  private _jsonError = '';

  static get observedAttributes(): string[] {
    return ['readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected || !this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('click', this._handleClick as EventListener);
    this.shadowRoot.addEventListener('change', this._handleChange as EventListener);
    this.shadowRoot.addEventListener('input', this._handleInput as EventListener);
    this.shadowRoot.addEventListener('dragstart', this._handleDragStart as EventListener);
    this.shadowRoot.addEventListener('dragover', this._handleDragOver as EventListener);
    this.shadowRoot.addEventListener('drop', this._handleDrop as EventListener);
    this.shadowRoot.addEventListener('dragend', this._handleDragEnd as EventListener);
    this.shadowRoot.addEventListener('toggle', this._handleToggle as EventListener);
    this.render();
  }

  disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener('click', this._handleClick as EventListener);
    this.shadowRoot?.removeEventListener('change', this._handleChange as EventListener);
    this.shadowRoot?.removeEventListener('input', this._handleInput as EventListener);
    this.shadowRoot?.removeEventListener('dragstart', this._handleDragStart as EventListener);
    this.shadowRoot?.removeEventListener('dragover', this._handleDragOver as EventListener);
    this.shadowRoot?.removeEventListener('drop', this._handleDrop as EventListener);
    this.shadowRoot?.removeEventListener('dragend', this._handleDragEnd as EventListener);
    this.shadowRoot?.removeEventListener('toggle', this._handleToggle as EventListener);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'readonly') {
      this._readonly = newValue !== null;
      this.render();
    }
  }

  get property(): string {
    return this._property;
  }

  set property(value: string | undefined) {
    this._property = value || '';
  }

  get value(): QdsActionPanelItem[] {
    return cloneValue(this._value);
  }

  set value(value: unknown) {
    this._value = parseItems(value);
    if (!this._modalOpen) {
      this._selectedIndex = this._value.length > 0 ? 0 : -1;
    }
    this.render();
  }

  get metadata(): QdsConfigField | undefined {
    return this._metadata;
  }

  set metadata(value: QdsConfigField | undefined) {
    this._metadata = value;
    this.render();
  }

  get context(): unknown {
    return this._context;
  }

  set context(value: unknown) {
    this._context = value;
  }

  get target(): Record<string, unknown> | undefined {
    return this._target;
  }

  set target(value: Record<string, unknown> | undefined) {
    this._target = value;
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  private _items(): QdsActionPanelItem[] {
    return parseItems(this._value);
  }

  private _selectedItem(): QdsActionPanelItem | null {
    if (this._selectedIndex < 0 || this._selectedIndex >= this._workingItems.length) {
      return null;
    }

    return this._workingItems[this._selectedIndex];
  }

  private _dispatchValue(items: QdsActionPanelItem[]): void {
    this._value = parseItems(items);
    this.dispatchEvent(new CustomEvent('value-change', {
      bubbles: true,
      composed: true,
      detail: {
        property: this._property,
        value: cloneValue(this._value)
      }
    }));
  }

  private _syncJsonDraft(): void {
    this._jsonDraft = JSON.stringify(this._workingItems, null, 2);
    this._jsonError = '';
    const jsonEditor = this.shadowRoot?.querySelector<HTMLTextAreaElement>('#json-editor');
    if (jsonEditor && document.activeElement !== jsonEditor) {
      jsonEditor.value = this._jsonDraft;
    }
  }

  private _openModal(): void {
    if (this._readonly) {
      return;
    }

    this._workingItems = cloneValue(this._items());
    this._selectedIndex = this._workingItems.length > 0 ? 0 : -1;
    this._modalOpen = true;
    this._advancedOpen = false;
    this._syncJsonDraft();
    this.render();
  }

  private _closeModal(): void {
    this._modalOpen = false;
    this._advancedOpen = false;
    this._activeDropIndex = null;
    this._activeDragPayload = null;
    this._workingItems = [];
    this._jsonError = '';
    this.render();
  }

  private _applyModal(): void {
    this._dispatchValue(this._workingItems);
    this._closeModal();
  }

  private _addItem(): void {
    this._workingItems.push(createDefaultItem());
    this._selectedIndex = this._workingItems.length - 1;
    this._syncJsonDraft();
    this.render();
  }

  private _duplicateSelectedItem(): void {
    const item = this._selectedItem();
    if (!item) {
      return;
    }

    const duplicate = cloneValue(item);
    this._workingItems.splice(this._selectedIndex + 1, 0, duplicate);
    this._selectedIndex += 1;
    this._syncJsonDraft();
    this.render();
  }

  private _removeItem(index: number): void {
    if (index < 0 || index >= this._workingItems.length) {
      return;
    }

    this._workingItems.splice(index, 1);
    if (this._workingItems.length === 0) {
      this._selectedIndex = -1;
    } else if (this._selectedIndex >= this._workingItems.length) {
      this._selectedIndex = this._workingItems.length - 1;
    } else if (index < this._selectedIndex) {
      this._selectedIndex -= 1;
    }

    this._syncJsonDraft();
    this.render();
  }

  private _selectItem(index: number): void {
    if (index < 0 || index >= this._workingItems.length || index === this._selectedIndex) {
      return;
    }

    this._selectedIndex = index;
    this.render();
  }

  private _applyJsonDraft(): void {
    try {
      const parsed = JSON.parse(this._jsonDraft);
      if (!Array.isArray(parsed)) {
        this._jsonError = 'JSON must resolve to an array of action items.';
        this._syncJsonError();
        return;
      }

      this._workingItems = parseItems(parsed);
      this._selectedIndex = this._workingItems.length > 0
        ? Math.max(0, Math.min(this._selectedIndex, this._workingItems.length - 1))
        : -1;
      this._jsonError = '';
      this._syncJsonDraft();
      this.render();
    } catch {
      this._jsonError = 'JSON is invalid. Fix the syntax before applying it.';
      this._syncJsonError();
    }
  }

  private _resetJsonDraft(): void {
    this._syncJsonDraft();
    this._syncJsonError();
  }

  private _syncJsonError(): void {
    const error = this.shadowRoot?.querySelector<HTMLElement>('[data-role="json-error"]');
    if (error) {
      error.textContent = this._jsonError;
      error.hidden = !this._jsonError;
    }
  }

  private _setActiveDropIndex(index: number | null): void {
    if (this._activeDropIndex === index) {
      return;
    }

    this._activeDropIndex = index;
    this.shadowRoot?.querySelectorAll('.action-panel-items-editor__drop-slot').forEach((slot) => {
      const slotIndex = Number.parseInt((slot as HTMLElement).dataset.dropIndex || '', 10);
      slot.classList.toggle('action-panel-items-editor__drop-slot--active', Number.isFinite(slotIndex) && slotIndex === index);
    });
  }

  private _eventElement(target: EventTarget | null): HTMLElement | null {
    if (target instanceof HTMLElement) {
      return target;
    }

    if (target instanceof Node) {
      return target.parentElement;
    }

    return null;
  }

  private _resolveDropIndex(
    target: EventTarget | null,
    clientY: number,
    payload: DragPayload | null = this._activeDragPayload
  ): number | null {
    const element = this._eventElement(target);
    const slot = element?.closest<HTMLElement>('[data-drop-index]');
    if (slot) {
      const slotIndex = Number.parseInt(slot.dataset.dropIndex || '', 10);
      return Number.isFinite(slotIndex) ? slotIndex : null;
    }

    const item = element?.closest<HTMLElement>('[data-item-index]');
    if (!item) {
      return null;
    }

    const itemIndex = Number.parseInt(item.dataset.itemIndex || '', 10);
    if (!Number.isFinite(itemIndex)) {
      return null;
    }

    const rect = item.getBoundingClientRect();
    const midpoint = rect.top + (rect.height / 2);
    const insideBounds = clientY >= rect.top && clientY <= rect.bottom;

    if (insideBounds && Math.abs(clientY - midpoint) > 6) {
      return clientY <= midpoint ? itemIndex : itemIndex + 1;
    }

    if (payload && payload.index !== itemIndex) {
      return payload.index < itemIndex ? itemIndex + 1 : itemIndex;
    }

    return clientY <= midpoint ? itemIndex : itemIndex + 1;
  }

  private _setDragPayload(event: DragEvent, payload: DragPayload): boolean {
    if (!event.dataTransfer) {
      return false;
    }

    event.dataTransfer.setData(ACTION_PANEL_ITEMS_MIME, JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'move';
    this._activeDragPayload = payload;
    return true;
  }

  private _getDragPayload(event: DragEvent): DragPayload | null {
    if (!event.dataTransfer) {
      return this._activeDragPayload;
    }

    try {
      const raw = event.dataTransfer.getData(ACTION_PANEL_ITEMS_MIME);
      if (!raw) {
        return this._activeDragPayload;
      }

      const parsed = JSON.parse(raw) as Partial<DragPayload>;
      if (typeof parsed.index === 'number') {
        return { index: parsed.index };
      }
    } catch (error) {
      console.error('Unable to read action panel item drag payload.', error);
    }

    return this._activeDragPayload;
  }

  private _bindDetailEditor(): void {
    if (!this.shadowRoot) {
      return;
    }

    const editor = this.shadowRoot.getElementById('item-editor') as any;
    const selectedItem = this._selectedItem();
    if (!editor || !selectedItem) {
      return;
    }

    editor.setAttribute('label', selectedItem.label || `Action ${this._selectedIndex + 1}`);
    editor.target = cloneValue(selectedItem);
    editor.metadata = {
      label: { displayName: 'Label', type: 'string' },
      href: { displayName: 'Link URL', type: 'string' },
      target: {
        displayName: 'Link Target',
        type: 'select',
        options: [
          { value: '_self', label: 'Same Tab' },
          { value: '_blank', label: 'New Tab' }
        ]
      },
      actionKind: {
        displayName: 'Action Kind',
        type: 'select',
        options: [
          { value: 'forward', label: 'Forward' },
          { value: 'external', label: 'External' },
          { value: 'download', label: 'Download' }
        ]
      },
      icon: { displayName: 'Icon', type: 'string', valueRole: 'icon', editorTag: 'qds-icon-picker' },
      iconSrc: { displayName: 'Icon URL', type: 'string' },
      iconAlt: { displayName: 'Icon Alt', type: 'string' }
    };
    editor.context = this._context;
    editor.readonly = this._readonly;
  }

  private _renderItemPreview(item: QdsActionPanelItem, mode: 'summary' | 'detail' = 'summary'): string {
    const showTarget = mode === 'summary';

    return `
      ${renderQdsIcon(item.icon, 'action-panel-items-editor__preview-icon') || (item.iconSrc
        ? `<img class="action-panel-items-editor__preview-icon" src="${escapeHtml(item.iconSrc)}" alt="${escapeHtml(item.iconAlt || `${item.label} icon`)}">`
        : '<span class="action-panel-items-editor__preview-icon action-panel-items-editor__preview-icon--empty" aria-hidden="true">Icon</span>')}
      <div class="action-panel-items-editor__preview-copy">
        <span class="action-panel-items-editor__preview-kind">${escapeHtml(showTarget ? (item.target === '_blank' ? 'New tab' : 'Same tab') : item.actionKind)}</span>
        <p class="action-panel-items-editor__preview-label">${escapeHtml(item.label || 'New action')}</p>
        <p class="action-panel-items-editor__preview-href">${escapeHtml(item.href || '#')}</p>
      </div>
      <span class="action-panel-items-editor__preview-arrow" aria-hidden="true">→</span>
    `;
  }

  private _syncSelectedPreview(): void {
    if (!this.shadowRoot) {
      return;
    }

    const selectedItem = this._selectedItem();
    if (!selectedItem) {
      return;
    }

    const preview = this.shadowRoot.querySelector<HTMLElement>('[data-role="selected-preview"]');
    if (preview) {
      preview.innerHTML = this._renderItemPreview(selectedItem, 'detail');
    }

    const title = this.shadowRoot.querySelector<HTMLElement>('[data-role="selected-title"]');
    if (title) {
      title.textContent = selectedItem.label || `Action ${this._selectedIndex + 1}`;
    }

    const listLabel = this.shadowRoot.querySelector<HTMLElement>(`[data-item-index="${this._selectedIndex}"] [data-role="item-label"]`);
    if (listLabel) {
      listLabel.textContent = selectedItem.label || `Action ${this._selectedIndex + 1}`;
    }

    const listHref = this.shadowRoot.querySelector<HTMLElement>(`[data-item-index="${this._selectedIndex}"] [data-role="item-href"]`);
    if (listHref) {
      listHref.textContent = selectedItem.href || '#';
    }

    const listKind = this.shadowRoot.querySelector<HTMLElement>(`[data-item-index="${this._selectedIndex}"] [data-role="item-kind"]`);
    if (listKind) {
      listKind.textContent = selectedItem.actionKind;
    }
  }

  private _handleClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const actionElement = target?.closest<HTMLElement>('[data-action]');
    if (!actionElement) {
      return;
    }

    const action = actionElement.dataset.action;

    if (action === 'open-modal') {
      this._openModal();
      return;
    }

    if (action === 'close-modal' || action === 'cancel-modal') {
      this._closeModal();
      return;
    }

    if (action === 'apply-modal') {
      this._applyModal();
      return;
    }

    if (action === 'add-item') {
      this._addItem();
      return;
    }

    if (action === 'duplicate-item') {
      this._duplicateSelectedItem();
      return;
    }

    if (action === 'remove-item') {
      const index = Number.parseInt(actionElement.dataset.removeIndex || '', 10);
      if (Number.isFinite(index)) {
        this._removeItem(index);
      }
      return;
    }

    if (action === 'select-item') {
      const index = Number.parseInt(actionElement.dataset.itemIndex || '', 10);
      if (Number.isFinite(index)) {
        this._selectItem(index);
      }
      return;
    }

    if (action === 'apply-json') {
      this._applyJsonDraft();
      return;
    }

    if (action === 'reset-json') {
      this._resetJsonDraft();
    }
  };

  private _handleInput = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (target?.id !== 'json-editor') {
      return;
    }

    this._jsonDraft = (target as HTMLTextAreaElement).value;
  };

  private _handleChange = (event: Event): void => {
    if (!(event instanceof CustomEvent) || !event.detail || !Object.prototype.hasOwnProperty.call(event.detail, 'target')) {
      return;
    }

    const editor = event.composedPath().find((node) => (
      node instanceof HTMLElement && node.id === 'item-editor'
    )) as HTMLElement | undefined;
    if (!editor) {
      return;
    }

    event.stopPropagation();
    const selectedItem = this._selectedItem();
    if (!selectedItem) {
      return;
    }

    this._workingItems[this._selectedIndex] = toActionPanelItem((event as CustomEvent).detail?.target || {});
    this._syncJsonDraft();
    this._syncSelectedPreview();
  };

  private _handleToggle = (event: Event): void => {
    const target = event.target as HTMLDetailsElement | null;
    if (target?.id !== 'advanced-json') {
      return;
    }

    this._advancedOpen = target.open;
  };

  private _handleDragStart = (event: DragEvent): void => {
    if (this._readonly) {
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLElement | null;
    const source = target?.closest<HTMLElement>('[data-item-drag-index]');
    if (!source) {
      return;
    }

    const index = Number.parseInt(source.dataset.itemDragIndex || '', 10);
    if (!Number.isFinite(index)) {
      event.preventDefault();
      return;
    }

    if (!this._setDragPayload(event, { index })) {
      event.preventDefault();
      return;
    }

    source.classList.add('action-panel-items-editor__grip--dragging');
  };

  private _handleDragOver = (event: DragEvent): void => {
    if (this._readonly || !this._modalOpen) {
      return;
    }

    const payload = this._getDragPayload(event);
    const dropIndex = this._resolveDropIndex(event.target, event.clientY, payload);
    if (!payload || dropIndex === null) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this._setActiveDropIndex(dropIndex);
  };

  private _handleDrop = (event: DragEvent): void => {
    if (this._readonly || !this._modalOpen) {
      return;
    }

    const payload = this._getDragPayload(event);
    const rawDropIndex = this._activeDropIndex ?? this._resolveDropIndex(event.target, event.clientY, payload);
    if (!payload || rawDropIndex === null) {
      return;
    }

    event.preventDefault();
    if (payload.index < 0 || payload.index >= this._workingItems.length) {
      this._setActiveDropIndex(null);
      this._activeDragPayload = null;
      return;
    }

    const [movedItem] = this._workingItems.splice(payload.index, 1);
    const nextIndex = rawDropIndex > payload.index ? rawDropIndex - 1 : rawDropIndex;
    this._workingItems.splice(nextIndex, 0, movedItem);
    this._selectedIndex = nextIndex;
    this._setActiveDropIndex(null);
    this._activeDragPayload = null;
    this._syncJsonDraft();
    this.render();
  };

  private _handleDragEnd = (): void => {
    this._activeDragPayload = null;
    this._setActiveDropIndex(null);
    this.shadowRoot?.querySelectorAll('.action-panel-items-editor__grip--dragging').forEach((item) => {
      item.classList.remove('action-panel-items-editor__grip--dragging');
    });
  };

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const items = this._items();
    const modalTitle = this._metadata?.displayName || 'Action Items';
    const configuredCount = items.length;
    const summaryLabels = configuredCount > 0
      ? items.slice(0, 3).map((item) => item.label || 'Untitled action').join(' • ')
      : 'Open the editor to add launch actions.';
    const summaryTitle = configuredCount === 1
      ? '1 action configured'
      : `${configuredCount} actions configured`;
    const selectedItem = this._selectedItem();

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        qds-property-editor {
          --qds-property-editor-margin-block: 0;
        }

        .action-panel-items-editor__summary {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          width: 100%;
          min-height: 44px;
          padding: 10px 12px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          color: inherit;
          text-align: left;
          font: inherit;
          cursor: ${this._readonly ? 'default' : 'pointer'};
        }

        .action-panel-items-editor__summary:focus-visible,
        .action-panel-items-editor__button:focus-visible,
        .action-panel-items-editor__list-item:focus-visible,
        .action-panel-items-editor__modal-close:focus-visible,
        .action-panel-items-editor__modal-button:focus-visible,
        .action-panel-items-editor__advanced-button:focus-visible {
          outline: 2px solid var(--input-focus-border, #4285f4);
          outline-offset: 2px;
        }

        .action-panel-items-editor__summary-copy {
          display: grid;
          gap: 4px;
          min-width: 0;
        }

        .action-panel-items-editor__summary-count {
          color: var(--theme-text-primary, #333333);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .action-panel-items-editor__summary-labels {
          color: var(--theme-text-secondary, #5f6368);
          font-size: 13px;
          line-height: 1.4;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .action-panel-items-editor__summary-cta {
          color: var(--theme-accent-primary, #0c4c87);
          font-size: 13px;
          font-weight: 600;
        }

        .action-panel-items-editor__modal-layer {
          position: fixed;
          inset: 0;
          z-index: 30;
          display: grid;
          place-items: center;
          padding: 24px;
        }

        .action-panel-items-editor__modal-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(15, 23, 42, 0.35);
        }

        .action-panel-items-editor__modal {
          position: relative;
          z-index: 1;
          display: grid;
          gap: 16px;
          width: min(1080px, calc(100vw - 32px));
          max-height: calc(100vh - 48px);
          padding: 18px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          box-shadow: var(--shadow-dropdown, 0 4px 12px rgba(0, 0, 0, 0.15));
          overflow: auto;
        }

        .action-panel-items-editor__modal-header,
        .action-panel-items-editor__modal-toolbar,
        .action-panel-items-editor__modal-footer {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .action-panel-items-editor__modal-header {
          justify-content: space-between;
        }

        .action-panel-items-editor__modal-title-group {
          display: grid;
          gap: 4px;
        }

        .action-panel-items-editor__modal-title {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .action-panel-items-editor__modal-meta {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: 13px;
        }

        .action-panel-items-editor__button,
        .action-panel-items-editor__modal-button,
        .action-panel-items-editor__modal-close,
        .action-panel-items-editor__advanced-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 36px;
          padding: 8px 12px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          color: inherit;
          font: inherit;
          cursor: ${this._readonly ? 'default' : 'pointer'};
        }

        .action-panel-items-editor__modal-button[data-variant="primary"] {
          background: var(--theme-accent-primary, #0c4c87);
          border-color: var(--theme-accent-primary, #0c4c87);
          color: #ffffff;
        }

        .action-panel-items-editor__button[disabled],
        .action-panel-items-editor__modal-button[disabled],
        .action-panel-items-editor__modal-close[disabled],
        .action-panel-items-editor__advanced-button[disabled] {
          cursor: default;
          opacity: 0.6;
        }

        .action-panel-items-editor__modal-toolbar {
          justify-content: space-between;
        }

        .action-panel-items-editor__modal-toolbar-group {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .action-panel-items-editor__workspace {
          display: grid;
          grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
          gap: 16px;
          align-items: start;
        }

        .action-panel-items-editor__panel {
          display: grid;
          gap: 12px;
          min-width: 0;
        }

        .action-panel-items-editor__panel-title {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .action-panel-items-editor__list {
          display: grid;
          gap: 0;
        }

        .action-panel-items-editor__drop-slot {
          height: 12px;
          border-top: 2px solid transparent;
          transition: border-color 0.15s ease;
        }

        .action-panel-items-editor__drop-slot--active {
          border-color: var(--input-focus-border, #4285f4);
        }

        .action-panel-items-editor__list-item {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr);
          gap: 10px;
          align-items: start;
          padding: 12px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          cursor: ${this._readonly ? 'default' : 'pointer'};
        }

        .action-panel-items-editor__list-item--selected {
          border-color: var(--input-focus-border, #4285f4);
          box-shadow: var(--shadow-focus, 0 1px 6px rgba(32, 33, 36, 0.2));
        }

        .action-panel-items-editor__grip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          min-height: 40px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          background: var(--theme-background-secondary, #f8f8f8);
          color: var(--theme-text-secondary, #5f6368);
          font-size: 16px;
          cursor: ${this._readonly ? 'default' : 'grab'};
          user-select: none;
          -webkit-user-select: none;
        }

        .action-panel-items-editor__grip--dragging {
          opacity: 0.55;
        }

        .action-panel-items-editor__list-copy {
          display: grid;
          gap: 4px;
          min-width: 0;
        }

        .action-panel-items-editor__list-kind {
          color: var(--theme-text-secondary, #5f6368);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .action-panel-items-editor__list-label {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.35;
        }

        .action-panel-items-editor__list-href {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          line-height: 1.4;
          word-break: break-all;
        }

        .action-panel-items-editor__empty {
          display: grid;
          place-items: center;
          min-height: 140px;
          padding: 16px;
          border: 1px dashed var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-secondary, #f8f8f8);
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          text-align: center;
        }

        .action-panel-items-editor__detail {
          display: grid;
          gap: 16px;
        }

        .action-panel-items-editor__detail-preview {
          display: grid;
          gap: 12px;
          padding: 14px;
          border: 1px solid rgba(12, 76, 135, 0.1);
          background: linear-gradient(112deg, rgba(255, 255, 255, 0.72) 27.27%, rgba(255, 255, 255, 0.92) 102.04%);
        }

        .action-panel-items-editor__detail-preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .action-panel-items-editor__detail-preview-title {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
        }

        .action-panel-items-editor__preview {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: center;
          gap: 12px;
        }

        .action-panel-items-editor__preview-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: var(--theme-background-secondary, #f8f8f8);
          border: 1px solid var(--theme-border-color, #e0e0e0);
          object-fit: cover;
        }

        svg.action-panel-items-editor__preview-icon {
          padding: 8px;
          color: var(--theme-text-secondary, #5f6368);
        }

        .action-panel-items-editor__preview-icon--empty {
          display: grid;
          place-items: center;
          color: var(--theme-text-tertiary, #7f8185);
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .action-panel-items-editor__preview-copy {
          display: grid;
          gap: 4px;
          min-width: 0;
        }

        .action-panel-items-editor__preview-kind {
          color: var(--theme-text-secondary, #5f6368);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .action-panel-items-editor__preview-label {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.35;
        }

        .action-panel-items-editor__preview-href {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          line-height: 1.4;
          word-break: break-all;
        }

        .action-panel-items-editor__preview-arrow {
          color: var(--theme-accent-primary, #0c4c87);
          font-size: 18px;
          font-weight: 600;
        }

        .action-panel-items-editor__advanced {
          border: 1px solid var(--theme-border-color, #e0e0e0);
          background: var(--theme-background-secondary, #f8f8f8);
        }

        .action-panel-items-editor__advanced summary {
          padding: 12px 14px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          user-select: none;
        }

        .action-panel-items-editor__advanced-body {
          display: grid;
          gap: 12px;
          padding: 0 14px 14px;
        }

        .action-panel-items-editor__json {
          width: 100%;
          min-height: 220px;
          padding: 12px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          color: var(--theme-text-primary, #333333);
          font: inherit;
          font-family: var(--theme-font-family-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
          font-size: 12px;
          line-height: 1.5;
          resize: vertical;
        }

        .action-panel-items-editor__json-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .action-panel-items-editor__json-error {
          margin: 0;
          color: #b42318;
          font-size: 12px;
          font-weight: 500;
        }

        .action-panel-items-editor__modal-footer {
          justify-content: flex-end;
        }

        @media (max-width: 960px) {
          .action-panel-items-editor__workspace {
            grid-template-columns: minmax(0, 1fr);
          }
        }
      </style>

      <button
        type="button"
        class="action-panel-items-editor__summary"
        data-action="open-modal"
        aria-label="${escapeHtml(modalTitle)} editor"
        title="${escapeHtml(summaryLabels)}"
        ${this._readonly ? 'disabled' : ''}
      >
        <span class="action-panel-items-editor__summary-copy">
          <span class="action-panel-items-editor__summary-count">${escapeHtml(summaryTitle)}</span>
          <span class="action-panel-items-editor__summary-labels">${escapeHtml(summaryLabels)}</span>
        </span>
        <span class="action-panel-items-editor__summary-cta">${this._readonly ? 'View' : 'Edit...'}</span>
      </button>

      ${this._modalOpen ? `
        <div class="action-panel-items-editor__modal-layer">
          <div class="action-panel-items-editor__modal-backdrop" data-action="cancel-modal"></div>
          <div class="action-panel-items-editor__modal" role="dialog" aria-modal="true" aria-label="${escapeHtml(modalTitle || 'Action items editor')}">
            <div class="action-panel-items-editor__modal-header">
              <div class="action-panel-items-editor__modal-title-group">
                <h3 class="action-panel-items-editor__modal-title">${escapeHtml(modalTitle || 'Action Items')}</h3>
                <p class="action-panel-items-editor__modal-meta">${escapeHtml(summaryTitle)}</p>
              </div>
              <button type="button" class="action-panel-items-editor__modal-close" data-action="close-modal">Close</button>
            </div>

            <div class="action-panel-items-editor__modal-toolbar">
              <div class="action-panel-items-editor__modal-toolbar-group">
                <button type="button" class="action-panel-items-editor__button" data-action="add-item" ${this._readonly ? 'disabled' : ''}>Add Action</button>
                <button type="button" class="action-panel-items-editor__button" data-action="duplicate-item" ${this._readonly || !selectedItem ? 'disabled' : ''}>Duplicate</button>
                <button type="button" class="action-panel-items-editor__button" data-action="remove-item" data-remove-index="${this._selectedIndex}" ${this._readonly || !selectedItem ? 'disabled' : ''}>Delete</button>
              </div>
              <div class="action-panel-items-editor__modal-toolbar-group">
                <span class="action-panel-items-editor__modal-meta">Drag from the grip to reorder.</span>
              </div>
            </div>

            <div class="action-panel-items-editor__workspace">
              <section class="action-panel-items-editor__panel">
                <h4 class="action-panel-items-editor__panel-title">Actions</h4>
                <div class="action-panel-items-editor__list" aria-label="Action list">
                  ${this._workingItems.length === 0 ? `
                    <div class="action-panel-items-editor__drop-slot ${this._activeDropIndex === 0 ? 'action-panel-items-editor__drop-slot--active' : ''}" data-drop-index="0"></div>
                    <div class="action-panel-items-editor__empty">No actions yet. Add one to start building the panel.</div>
                  ` : this._workingItems.map((item, index) => `
                    <div class="action-panel-items-editor__drop-slot ${this._activeDropIndex === index ? 'action-panel-items-editor__drop-slot--active' : ''}" data-drop-index="${index}"></div>
                    <article
                      class="action-panel-items-editor__list-item ${index === this._selectedIndex ? 'action-panel-items-editor__list-item--selected' : ''}"
                      data-action="select-item"
                      data-item-index="${index}"
                      tabindex="${this._readonly ? '-1' : '0'}"
                    >
                      <div
                        class="action-panel-items-editor__grip"
                        data-item-drag-index="${index}"
                        draggable="${this._readonly ? 'false' : 'true'}"
                        aria-label="Drag action"
                        title="Drag to reorder"
                      >⋮⋮</div>
                      <div class="action-panel-items-editor__list-copy">
                        <span class="action-panel-items-editor__list-kind" data-role="item-kind">${escapeHtml(item.actionKind)}</span>
                        <p class="action-panel-items-editor__list-label" data-role="item-label">${escapeHtml(item.label || `Action ${index + 1}`)}</p>
                        <p class="action-panel-items-editor__list-href" data-role="item-href">${escapeHtml(item.href || '#')}</p>
                      </div>
                    </article>
                  `).join('')}
                  <div class="action-panel-items-editor__drop-slot ${this._activeDropIndex === this._workingItems.length ? 'action-panel-items-editor__drop-slot--active' : ''}" data-drop-index="${this._workingItems.length}"></div>
                </div>
              </section>

              <section class="action-panel-items-editor__panel">
                <h4 class="action-panel-items-editor__panel-title">Selected Action</h4>
                ${selectedItem ? `
                  <div class="action-panel-items-editor__detail">
                    <div class="action-panel-items-editor__detail-preview">
                      <div class="action-panel-items-editor__detail-preview-header">
                        <p class="action-panel-items-editor__detail-preview-title" data-role="selected-title">${escapeHtml(selectedItem.label || `Action ${this._selectedIndex + 1}`)}</p>
                      </div>
                      <div class="action-panel-items-editor__preview" data-role="selected-preview">
                        ${this._renderItemPreview(selectedItem, 'detail')}
                      </div>
                    </div>
                    <qds-property-editor id="item-editor" expanded></qds-property-editor>
                  </div>
                ` : `
                  <div class="action-panel-items-editor__empty">Select an action to edit its fields.</div>
                `}
              </section>
            </div>

            <details id="advanced-json" class="action-panel-items-editor__advanced" ${this._advancedOpen ? 'open' : ''}>
              <summary>Advanced JSON</summary>
              <div class="action-panel-items-editor__advanced-body">
                <textarea id="json-editor" class="action-panel-items-editor__json" aria-label="Advanced JSON editor">${escapeHtml(this._jsonDraft)}</textarea>
                <div class="action-panel-items-editor__json-actions">
                  <p class="action-panel-items-editor__json-error" data-role="json-error" ${this._jsonError ? '' : 'hidden'}>${escapeHtml(this._jsonError)}</p>
                  <div class="action-panel-items-editor__modal-toolbar-group">
                    <button type="button" class="action-panel-items-editor__advanced-button" data-action="reset-json">Reset</button>
                    <button type="button" class="action-panel-items-editor__advanced-button" data-action="apply-json">Apply JSON</button>
                  </div>
                </div>
              </div>
            </details>

            <div class="action-panel-items-editor__modal-footer">
              <button type="button" class="action-panel-items-editor__modal-button" data-action="cancel-modal">Cancel</button>
              <button type="button" class="action-panel-items-editor__modal-button" data-variant="primary" data-action="apply-modal">Apply</button>
            </div>
          </div>
        </div>
      ` : ''}
    `;

    this._bindDetailEditor();
    this._syncJsonError();
    void adoptPrimeIconsStyles(this.shadowRoot);
    hydrateQdsLucideIcons(this.shadowRoot);
  }
}

if (!customElements.get('qds-action-panel-items-editor')) {
  customElements.define('qds-action-panel-items-editor', QdsActionPanelItemsEditor);
}

export default QdsActionPanelItemsEditor;
